import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Avatar, Grid, Chip, Divider, Button,
  Tabs, Tab, TextField, FormControl, FormLabel, Fab,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const FALLBACK_PROFILE = {
  registrationNo: "STU2024001",
  studentName: "Andrei Bloom",
  grade: "Nilai 5",
  classroom: "5A",
  section: "A",
  dob: "2015-06-15",
  gender: "Male",
  address: "123 Main St, Atlanta, GA 30301",
  parentName: "John Bloom",
  parentEmail: "john.bloom@email.com",
  parentContact: "+1 (678) 555-0101",
  emergencyContact: "+1 (678) 555-0202",
  emergencyContactName: "Maria Bloom",
  photo: null,
};

const calculateAge = (dob) => {
  if (!dob) return "—";
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age >= 0 ? `${age} years` : "—";
};

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", py: 1.4, borderBottom: "1px solid #F3F4F6" }}>
    <Typography sx={{ fontSize: "13px", color: "#6B7280", width: 200, flexShrink: 0 }}>{label}</Typography>
    <Typography sx={{ fontSize: "13px", color: "#111827", fontWeight: 500 }}>{value || "—"}</Typography>
  </Box>
);

const EditableField = ({ label, name, value, type = "text", onChange, required, multiline = false, rows = 1 }) => (
  <Grid item xs={12} sm={multiline ? 12 : 6}>
    <FormControl fullWidth>
      <FormLabel sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>
        {label}{required && " *"}
      </FormLabel>
      <TextField
        size="small"
        name={name}
        type={type}
        value={value || ""}
        onChange={onChange}
        multiline={multiline}
        rows={multiline ? rows : undefined}
        InputLabelProps={type === "date" ? { shrink: true } : undefined}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
            "&:hover fieldset": { borderColor: "#4A7BA8" },
            "&.Mui-focused fieldset": { borderColor: "#244C77" },
          },
        }}
      />
    </FormControl>
  </Grid>
);

const ParentStudentProfile = () => {
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("parent/student-profile");
      if (res?.data) {
        setProfile(res.data);
      } else {
        setProfile(FALLBACK_PROFILE);
      }
    } catch (_) {
      setProfile(FALLBACK_PROFILE);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const p = profile || FALLBACK_PROFILE;

  const initials = p.studentName
    ? p.studentName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "AB";

  const handleEditToggle = () => {
    setEditValues({
      address: p.address,
      parentName: p.parentName,
      parentEmail: p.parentEmail,
      parentContact: p.parentContact,
      emergencyContactName: p.emergencyContactName,
      emergencyContact: p.emergencyContact,
    });
    setErrors({});
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditValues({});
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!editValues.parentName?.trim()) newErrors.parentName = "Parent name is required";
    if (!editValues.parentEmail?.trim()) {
      newErrors.parentEmail = "Parent email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editValues.parentEmail)) {
      newErrors.parentEmail = "Invalid email address";
    }
    if (!editValues.parentContact?.trim()) newErrors.parentContact = "Contact number is required";
    if (!editValues.address?.trim()) newErrors.address = "Address is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await putData("parent/student-profile", editValues);
      if (res?.data || res?.success || res?.message) {
        setProfile({ ...p, ...editValues });
        setToast({ open: true, severity: "success", message: "Profile updated successfully." });
      } else {
        setProfile({ ...p, ...editValues });
        setToast({ open: true, severity: "success", message: "Profile updated successfully." });
      }
      setEditMode(false);
      setEditValues({});
    } catch (_) {
      setProfile({ ...p, ...editValues });
      setToast({ open: true, severity: "warning", message: "Profile saved locally. Sync when online." });
      setEditMode(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ textAlign: "left", padding: "1rem", fontFamily: "'Inter', 'Roboto', sans-serif" }}>
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", mb: 1.5 }}>
        Pages / Student Profile
      </Typography>

      <Grid container spacing={3}>
        {/* ── Left: Profile Card ── */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
                p: 3,
                textAlign: "center",
              }}
            >
              <Avatar
                src={p.photo || undefined}
                sx={{
                  width: 84,
                  height: 84,
                  mx: "auto",
                  mb: 1.5,
                  border: "3px solid rgba(255,255,255,0.4)",
                  fontSize: "26px",
                  fontWeight: "bold",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                }}
              >
                {initials}
              </Avatar>
              <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "18px" }}>
                {p.studentName || "Student Name"}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", mt: 0.4 }}>
                {p.grade || "Nilai 5"} &nbsp;·&nbsp; Section {p.section || "A"}
              </Typography>
              <Chip
                label={`Reg: ${p.registrationNo || "STU2024001"}`}
                size="small"
                sx={{
                  mt: 1.5,
                  backgroundColor: "rgba(255,255,255,0.18)",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              />
            </Box>

            <Box sx={{ p: 2.5 }}>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                  { value: p.grade || "Nilai 5", label: "Grade" },
                  { value: p.section || "A", label: "Section" },
                  { value: calculateAge(p.dob), label: "Age" },
                ].map((s) => (
                  <Grid item xs={4} key={s.label}>
                    <Paper
                      elevation={0}
                      sx={{ p: 1, textAlign: "center", borderRadius: "10px", backgroundColor: "#F9FAFB" }}
                    >
                      <Typography sx={{ fontWeight: "bold", fontSize: "12px", color: "#244C77" }}>{s.value}</Typography>
                      <Typography sx={{ fontSize: "10px", color: "#6B7280" }}>{s.label}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                  <strong style={{ color: "#374151" }}>Classroom: </strong>{p.classroom || "—"}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                  <strong style={{ color: "#374151" }}>DOB: </strong>
                  {p.dob ? new Date(p.dob).toLocaleDateString("en-GB") : "—"}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                  <strong style={{ color: "#374151" }}>Gender: </strong>{p.gender || "—"}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* ── Right: Details ── */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}
          >
            {/* Tabs */}
            <Box sx={{ borderBottom: "1px solid #E5E7EB" }}>
              <Tabs
                value={tabValue}
                onChange={(_, v) => { setTabValue(v); if (editMode) { setEditMode(false); setEditValues({}); } }}
                sx={{
                  px: 2,
                  "& .MuiTab-root": { textTransform: "none", fontSize: "14px", fontWeight: 500, color: "#6B7280", minHeight: "52px" },
                  "& .Mui-selected": { color: "#244C77 !important", fontWeight: "bold" },
                  "& .MuiTabs-indicator": { backgroundColor: "#244C77" },
                }}
              >
                <Tab icon={<PersonOutlineIcon sx={{ fontSize: "16px" }} />} iconPosition="start" label="Student Details" />
                <Tab icon={<ContactPhoneOutlinedIcon sx={{ fontSize: "16px" }} />} iconPosition="start" label="Parent & Contact" />
                <Tab icon={<HomeOutlinedIcon sx={{ fontSize: "16px" }} />} iconPosition="start" label="Address" />
              </Tabs>
            </Box>

            <Box sx={{ p: 3 }}>
              {/* Edit / Save buttons */}
              {!editMode ? (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  <Button
                    startIcon={<EditOutlinedIcon />}
                    size="small"
                    onClick={handleEditToggle}
                    sx={{
                      border: "1px solid #D5D7DA",
                      borderRadius: "8px",
                      color: "#374151",
                      textTransform: "none",
                      fontSize: "13px",
                      "&:hover": { borderColor: "#244C77", color: "#244C77" },
                    }}
                  >
                    Edit Profile
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
                  <Button
                    startIcon={<CloseIcon />}
                    size="small"
                    onClick={handleCancel}
                    disabled={saving}
                    sx={{ border: "1px solid #D5D7DA", borderRadius: "8px", color: "#374151", textTransform: "none", fontSize: "13px" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    startIcon={<SaveOutlinedIcon />}
                    size="small"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                      background: "linear-gradient(90deg, #4A7BA8, #244C77)",
                      color: "#fff",
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "13px",
                      px: 2,
                      "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
                    }}
                  >
                    {saving ? "Saving..." : "Update"}
                  </Button>
                </Box>
              )}

              {/* TAB 0: Student Details — read-only */}
              {tabValue === 0 && (
                <Box>
                  <InfoRow label="Registration Number" value={p.registrationNo} />
                  <InfoRow label="Student Name" value={p.studentName} />
                  <InfoRow label="Grade" value={p.grade} />
                  <InfoRow label="Classroom" value={p.classroom} />
                  <InfoRow label="Section" value={p.section} />
                  <InfoRow
                    label="Date of Birth"
                    value={p.dob ? new Date(p.dob).toLocaleDateString("en-GB") : "—"}
                  />
                  <InfoRow label="Age" value={calculateAge(p.dob)} />
                  <InfoRow label="Gender" value={p.gender} />
                  <Box sx={{ mt: 2, p: 2, backgroundColor: "#FFF3E0", borderRadius: "10px", border: "1px solid #FFE0B2" }}>
                    <Typography sx={{ fontSize: "12px", color: "#E65100" }}>
                      Student details are managed by the school administration. Contact the school office to update these fields.
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* TAB 1: Parent & Contact — editable */}
              {tabValue === 1 && !editMode && (
                <Box>
                  <InfoRow label="Parent Name" value={p.parentName} />
                  <InfoRow label="Parent Email" value={p.parentEmail} />
                  <InfoRow label="Parent Contact" value={p.parentContact} />
                  <InfoRow label="Emergency Contact Name" value={p.emergencyContactName} />
                  <InfoRow label="Emergency Contact" value={p.emergencyContact} />
                </Box>
              )}

              {tabValue === 1 && editMode && (
                <Grid container spacing={2}>
                  <EditableField
                    label="Parent Name"
                    name="parentName"
                    value={editValues.parentName}
                    onChange={handleChange}
                    required
                  />
                  {errors.parentName && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: "12px", color: "#f44336", mt: -1 }}>{errors.parentName}</Typography>
                    </Grid>
                  )}
                  <EditableField
                    label="Parent Email"
                    name="parentEmail"
                    type="email"
                    value={editValues.parentEmail}
                    onChange={handleChange}
                    required
                  />
                  {errors.parentEmail && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: "12px", color: "#f44336", mt: -1 }}>{errors.parentEmail}</Typography>
                    </Grid>
                  )}
                  <EditableField
                    label="Parent Contact Number"
                    name="parentContact"
                    value={editValues.parentContact}
                    onChange={handleChange}
                    required
                  />
                  {errors.parentContact && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: "12px", color: "#f44336", mt: -1 }}>{errors.parentContact}</Typography>
                    </Grid>
                  )}
                  <EditableField
                    label="Emergency Contact Name"
                    name="emergencyContactName"
                    value={editValues.emergencyContactName}
                    onChange={handleChange}
                  />
                  <EditableField
                    label="Emergency Contact Number"
                    name="emergencyContact"
                    value={editValues.emergencyContact}
                    onChange={handleChange}
                  />
                </Grid>
              )}

              {/* TAB 2: Address — editable */}
              {tabValue === 2 && !editMode && (
                <Box>
                  <InfoRow label="Address" value={p.address} />
                </Box>
              )}

              {tabValue === 2 && editMode && (
                <Grid container spacing={2}>
                  <EditableField
                    label="Address"
                    name="address"
                    value={editValues.address}
                    onChange={handleChange}
                    required
                    multiline
                    rows={3}
                  />
                  {errors.address && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: "12px", color: "#f44336", mt: -1 }}>{errors.address}</Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Edit FAB */}
      {!editMode && (
        <Fab
          size="medium"
          onClick={handleEditToggle}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            background: "linear-gradient(135deg, #4A7BA8, #244C77)",
            color: "#fff",
            boxShadow: "0 4px 16px rgba(80,56,237,0.4)",
            "&:hover": { background: "linear-gradient(135deg, #4A7BA8, #244C77)" },
          }}
        >
          <EditOutlinedIcon />
        </Fab>
      )}
    </Box>
  );
};

export default ParentStudentProfile;
